import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { toast } from "sonner";
import { Plus, Trash2, DollarSign, TrendingUp, TrendingDown, FileText } from "lucide-react";

const INCOME_CATEGORIES = ["event_fees", "roping_fees", "arena_fees", "other_income"];
const EXPENSE_CATEGORIES = ["arena_rent", "office", "helpers", "fuel", "other_expense"];

const FinancialPage = () => {
  const [playdays, setPlaydays] = useState([]);
  const [selectedPlayday, setSelectedPlayday] = useState("");
  const [report, setReport] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    type: "expense",
    category: "arena_rent",
    description: "",
    amount: ""
  });

  useEffect(() => {
    fetchPlaydays();
  }, []);

  useEffect(() => {
    if (selectedPlayday) {
      fetchReport();
      fetchRecords();
    }
  }, [selectedPlayday]);

  const fetchPlaydays = async () => {
    try {
      const response = await axios.get(`${API}/playdays`);
      setPlaydays(response.data);
      if (response.data.length > 0) {
        setSelectedPlayday(response.data[0].id);
      }
    } catch (error) {
      toast.error("Failed to fetch playdays");
    } finally {
      setLoading(false);
    }
  };

  const fetchReport = async () => {
    try {
      const response = await axios.get(`${API}/financial/report/${selectedPlayday}`);
      setReport(response.data);
    } catch (error) {
      toast.error("Failed to fetch financial report");
    }
  };

  const fetchRecords = async () => {
    try {
      const response = await axios.get(`${API}/financial?playday_id=${selectedPlayday}`);
      setRecords(response.data);
    } catch (error) {
      toast.error("Failed to fetch records");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/financial`, {
        playday_id: selectedPlayday,
        type: formData.type,
        category: formData.category,
        description: formData.description,
        amount: parseFloat(formData.amount)
      });
      toast.success("Record added");
      setIsDialogOpen(false);
      setFormData({ type: "expense", category: "arena_rent", description: "", amount: "" });
      fetchReport();
      fetchRecords();
    } catch (error) {
      toast.error("Failed to add record");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    try {
      await axios.delete(`${API}/financial/${id}`);
      toast.success("Record deleted");
      fetchReport();
      fetchRecords();
    } catch (error) {
      toast.error("Failed to delete record");
    }
  };

  const formatCategory = (cat) => {
    return cat.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="financial-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl text-text-primary">Financial Report</h1>
          <p className="text-text-secondary mt-1">Income, expenses, and profit/loss</p>
        </div>
      </div>

      {/* Controls */}
      <div className="flex gap-4 items-end">
        <div className="w-64">
          <label className="text-sm font-medium text-text-primary mb-2 block">Select Play Day</label>
          <Select value={selectedPlayday} onValueChange={setSelectedPlayday}>
            <SelectTrigger data-testid="playday-select">
              <SelectValue placeholder="Select a play day" />
            </SelectTrigger>
            <SelectContent>
              {playdays.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name} ({p.date})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary text-primary-foreground hover:bg-primary-hover" data-testid="add-record-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Income/Expense
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">Add Financial Record</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="financial-form">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value) => setFormData({ 
                    ...formData, 
                    type: value,
                    category: value === "income" ? "other_income" : "arena_rent"
                  })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="income">Income</SelectItem>
                    <SelectItem value="expense">Expense</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {formData.type === "income" ? (
                      INCOME_CATEGORIES.map((cat) => (
                        <SelectItem key={cat} value={cat}>{formatCategory(cat)}</SelectItem>
                      ))
                    ) : (
                      EXPENSE_CATEGORIES.map((cat) => (
                        <SelectItem key={cat} value={cat}>{formatCategory(cat)}</SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Amount ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0.00"
                  required
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-primary text-primary-foreground hover:bg-primary-hover" data-testid="financial-submit">
                  Add Record
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      {report && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-status-success/30">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-status-success/20 text-status-success">
                  <TrendingUp className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-text-secondary text-sm">Total Income</p>
                  <p className="font-heading font-bold text-2xl text-status-success">
                    ${report.total_income.toFixed(2)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-status-error/30">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-status-error/20 text-status-error">
                  <TrendingDown className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-text-secondary text-sm">Total Expenses</p>
                  <p className="font-heading font-bold text-2xl text-status-error">
                    ${report.total_expenses.toFixed(2)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={report.profit_loss >= 0 ? "border-status-success/30" : "border-status-error/30"}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  report.profit_loss >= 0 ? "bg-status-success/20 text-status-success" : "bg-status-error/20 text-status-error"
                }`}>
                  <DollarSign className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-text-secondary text-sm">Profit/Loss</p>
                  <p className={`font-heading font-bold text-2xl ${
                    report.profit_loss >= 0 ? "text-status-success" : "text-status-error"
                  }`}>
                    {report.profit_loss >= 0 ? "+" : ""}${report.profit_loss.toFixed(2)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Income & Expenses Tables */}
      {report && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Income */}
          <Card className="border-primary/20">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2 text-status-success">
                <TrendingUp className="h-5 w-5" />
                Income
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="bg-secondary/50">
                    <TableHead className="font-heading font-bold text-text-primary">Category</TableHead>
                    <TableHead className="font-heading font-bold text-text-primary text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.income.map((item, idx) => (
                    <TableRow key={idx} className="hover:bg-surface-muted">
                      <TableCell>
                        <div>
                          <p className="font-medium text-text-primary">{formatCategory(item.category)}</p>
                          <p className="text-xs text-text-muted">{item.description}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono font-bold text-status-success">
                        ${item.amount.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Expenses */}
          <Card className="border-primary/20">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2 text-status-error">
                <TrendingDown className="h-5 w-5" />
                Expenses
              </CardTitle>
            </CardHeader>
            <CardContent>
              {report.expenses.length === 0 ? (
                <div className="text-center py-8 text-text-secondary">
                  <FileText className="h-8 w-8 mx-auto mb-2 text-text-muted" />
                  No expenses recorded
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-secondary/50">
                      <TableHead className="font-heading font-bold text-text-primary">Category</TableHead>
                      <TableHead className="font-heading font-bold text-text-primary text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.expenses.map((item, idx) => (
                      <TableRow key={idx} className="hover:bg-surface-muted">
                        <TableCell>
                          <div>
                            <p className="font-medium text-text-primary">{formatCategory(item.category)}</p>
                            <p className="text-xs text-text-muted">{item.description}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-mono font-bold text-status-error">
                          ${item.amount.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Manual Records */}
      {records.length > 0 && (
        <Card className="border-primary/20">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Manual Records</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/50">
                  <TableHead className="font-heading font-bold text-text-primary">Type</TableHead>
                  <TableHead className="font-heading font-bold text-text-primary">Category</TableHead>
                  <TableHead className="font-heading font-bold text-text-primary">Description</TableHead>
                  <TableHead className="font-heading font-bold text-text-primary text-right">Amount</TableHead>
                  <TableHead className="font-heading font-bold text-text-primary text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {records.map((record) => (
                  <TableRow key={record.id} className="hover:bg-surface-muted">
                    <TableCell>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        record.type === "income" 
                          ? "bg-status-success/20 text-status-success" 
                          : "bg-status-error/20 text-status-error"
                      }`}>
                        {record.type}
                      </span>
                    </TableCell>
                    <TableCell className="text-text-primary">{formatCategory(record.category)}</TableCell>
                    <TableCell className="text-text-secondary">{record.description}</TableCell>
                    <TableCell className={`text-right font-mono font-bold ${
                      record.type === "income" ? "text-status-success" : "text-status-error"
                    }`}>
                      ${record.amount.toFixed(2)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(record.id)}
                        className="h-8 w-8 p-0 text-status-error hover:text-status-error"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default FinancialPage;
